import { useEffect, useState, lazy, Suspense } from 'react';
import { RequirementsForm } from './RequirementsForm';
import { SmartPlanningInterface } from './SmartPlanningInterface';
import { ConversationalRequirements } from './ConversationalRequirements';
import { PromptSidebar } from './PromptSidebar';
import { DrillDownPromptInterface } from './DrillDownPromptInterface';
import { WireframeEditor } from './WireframeEditor';
import { WireframeGallery } from './WireframeGallery';
import { AnalysisProgressIndicator } from './AnalysisProgressIndicator';
import { DesignSystemConfig } from './DesignSystemConfig';
const BlueprintDashboard = lazy(() =>
  import('./BlueprintDashboard').then(m => ({ default: m.BlueprintDashboard }))
);
import { ManifestViewer } from './ManifestViewer';
import { JiraPlanViewer } from './JiraPlanViewer';
import { GitHubScaffoldPanel } from './GitHubScaffoldPanel';
import { PlanModeErrorBoundary } from './ErrorBoundary';
import { HelpPanel } from './HelpPanel';
import { LoadingOverlay } from './LoadingOverlay';
import { ProgressStepper } from './ProgressStepper';
import { ConfirmModal } from '../Common/ConfirmModal';
import { usePlanMode } from '../../hooks/usePlanMode';
// Services will be imported dynamically when needed
import { supabase } from '../../lib/supabase';
import { logger } from '../../services/loggingService';
import toast from 'react-hot-toast';
// Unified UI Components
import { Heading, Text } from '../ui/Typography';
import { Button } from '../ui/Button';
import { Card, CardContent } from '../ui/Card';
import { LoadingState } from '../Common/LoadingState';
import { cn } from '../../utils/cn';
import { spacing } from '../../utils/spacing';

interface PlanModeDashboardProps {
  projectId: string;
  userId: string;
  jiraProjectKey?: string;
  onBuildModeStarted?: (sessionId: string) => void;
}

export function PlanModeDashboard({
  projectId,
  userId,
  jiraProjectKey = 'PROJ',
  onBuildModeStarted,
}: PlanModeDashboardProps) {
  const {
    currentStep,
    blueprintId,
    manifestId,
    isGenerating,
    setCurrentStep,
    setBlueprintId,
    setManifestId,
    setIsGenerating,
    saveToLocalStorage,
    loadFromLocalStorage,
  } = usePlanMode();

  const [designSystemId, setDesignSystemId] = useState<string | null>(null);
  const [wireframes, setWireframes] = useState<any[]>([]);
  const [requirementsMode, setRequirementsMode] = useState<
    'form' | 'conversational'
  >('conversational');
  const [_focusedPrompt, setFocusedPrompt] = useState<string>('');
  const [wireframeMode, setWireframeMode] = useState<'gallery' | 'editor'>(
    'gallery'
  );

  // Drill-down interface state
  const [selectedNode, setSelectedNode] = useState<any>(null);
  const [nodeData, setNodeData] = useState<any>(null);
  const [promptContext, setPromptContext] = useState<string>('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showDrillDown, setShowDrillDown] = useState(false);
  const [editingWireframe, setEditingWireframe] = useState<any>(null);

  const [project, setProject] = useState<any>(null);
  const [loadingProject, setLoadingProject] = useState(true);

  // Manifest confirmation modal state
  const [manifestConfirm, setManifestConfirm] = useState<{
    isOpen: boolean;
    resolvePromise: ((value: boolean) => void) | null;
  }>({
    isOpen: false,
    resolvePromise: null,
  });

  // Drill-down interface handlers
  const handleNodeSelect = (node: any, data: any, context: string) => {
    setSelectedNode(node);
    setNodeData(data);
    setPromptContext(context);
    setSuggestions([]); // Will be populated by the interface
    setShowDrillDown(true);
  };

  const handleNodeUpdate = (nodeId: string, updatedData: any) => {
    // Update the node data and refresh the sidebar
    setNodeData(updatedData);
    // Trigger a refresh of the blueprint tree
    // This would typically involve calling the BlueprintTreeService again
  };

  const handleCloseDrillDown = () => {
    setShowDrillDown(false);
    setSelectedNode(null);
    setNodeData(null);
    setPromptContext('');
    setSuggestions([]);
  };

  // Load project and check import status
  useEffect(() => {
    loadProject();
  }, [projectId]);

  const loadProject = async () => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .maybeSingle();

      if (error) {
        logger.error('Error loading project', { error, projectId });
        toast.error('Failed to load project');
        return;
      }

      setProject(data || null);
    } catch (error) {
      logger.error('Failed to load project', { error, projectId });
    } finally {
      setLoadingProject(false);
    }
  };

  // Load saved state on mount
  useEffect(() => {
    loadFromLocalStorage();
  }, [loadFromLocalStorage]);

  // Auto-save state changes
  useEffect(() => {
    saveToLocalStorage();
  }, [currentStep, blueprintId, manifestId, saveToLocalStorage]);

  const handleDesignSystemComplete = (id: string) => {
    setDesignSystemId(id);
    setCurrentStep('blueprint');
    toast.success('Design system configured! Now generating blueprint...');
  };

  const handleBlueprintGenerated = async (id: string) => {
    setBlueprintId(id);

    // Auto-generate low-fidelity wireframes
    if (designSystemId) {
      try {
        const screens = extractScreensFromBlueprint(id);
        const generatedWireframes =
          await WireframeGeneratorService.generateLowFidelityWireframes({
            projectId,
            blueprintId: id,
            designSystemId,
            fidelityLevel: 'low',
            screens,
          });

        setWireframes(generatedWireframes);
        setCurrentStep('wireframes-low');
        toast.success(
          'Low-fidelity wireframes generated! Review and edit as needed.'
        );
      } catch (error) {
        logger.error('Failed to generate wireframes', { error, projectId });
        toast.error('Failed to generate wireframes');
        setCurrentStep('blueprint');
      }
    } else {
      setCurrentStep('blueprint');
    }

    toast.success('Blueprint generated! Review and freeze when ready.');
  };

  const _handleWireframesApproved = async () => {
    try {
      // Upgrade all wireframes to high fidelity
      for (const wireframe of wireframes) {
        await WireframeGeneratorService.upgradeToHighFidelity(wireframe.id);
      }

      setCurrentStep('wireframes-high');
      toast.success('Wireframes upgraded to high fidelity!');
    } catch (error) {
      logger.error('Failed to upgrade wireframes', { error, wireframeId });
      toast.error('Failed to upgrade wireframes');
    }
  };

  const handleCreateWireframe = () => {
    setEditingWireframe(null);
    setWireframeMode('editor');
  };

  const handleEditWireframe = (wireframe: any) => {
    setEditingWireframe(wireframe);
    setWireframeMode('editor');
  };

  const handleViewWireframe = (wireframe: any) => {
    // Open wireframe in view mode
    setEditingWireframe(wireframe);
    setWireframeMode('editor');
  };

  const handleWireframeSaved = (_wireframe: any) => {
    setWireframeMode('gallery');
    setEditingWireframe(null);
    // Refresh wireframes list
    loadWireframes();
  };

  const handleCloseWireframeEditor = () => {
    setWireframeMode('gallery');
    setEditingWireframe(null);
  };

  const loadWireframes = async () => {
    try {
      const { RequirementsService } = await import(
        '../../services/requirementsService'
      );
      const wireframesData = await RequirementsService.getWireframes(projectId);
      setWireframes(wireframesData);
    } catch (error) {
      logger.error('Error loading wireframes', { error, projectId });
    }
  };

  const extractScreensFromBlueprint = (_blueprintId: string) => {
    // This would extract screen information from the blueprint
    // For now, return a basic set of screens
    return [
      {
        name: 'Home Page',
        type: 'page',
        route: '/',
        description: 'Main landing page',
      },
      {
        name: 'Dashboard',
        type: 'page',
        route: '/dashboard',
        description: 'User dashboard',
      },
      {
        name: 'Login',
        type: 'page',
        route: '/login',
        description: 'User authentication',
      },
    ];
  };

  const handleBlueprintFrozen = () => {
    toast.success(
      'Blueprint is now frozen. Ready to generate manifest and Jira plan.'
    );
  };

  const handleGenerateManifest = async (blueprintId: string) => {
    try {
      setIsGenerating(true);
      const manifest = await ManifestGeneratorService.generateFromBlueprint(
        blueprintId,
        userId
      );
      setManifestId(manifest.id);
      setCurrentStep('manifest');
      toast.success('Manifest generated successfully!');
    } catch (error) {
      logger.error('Failed to generate manifest', { error, projectId });
      toast.error((error as Error).message || 'Failed to generate manifest');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateJiraPlan = () => {
    setCurrentStep('jira');
  };

  const handleScaffold = () => {
    setCurrentStep('scaffold');
  };

  const handleStartBuildMode = async (blueprintId: string) => {
    try {
      // Comprehensive validation before starting Build Mode

      // 1. Check blueprint exists and is locked
      const { data: blueprint, error: blueprintError } = await supabase
        .from('project_blueprints')
        .select('is_locked, project_id')
        .eq('id', blueprintId)
        .maybeSingle();

      if (blueprintError) {
        logger.error('Error checking blueprint status', {
          error: blueprintError,
          blueprintId,
        });
        toast.error('Failed to verify blueprint status');
        return;
      }

      if (!blueprint) {
        toast.error('Blueprint not found');
        return;
      }

      if (!blueprint.is_locked) {
        toast.error(
          'Blueprint must be locked before starting Build Mode. Please lock the blueprint first.'
        );
        return;
      }

      // 2. Check manifest exists (optional but recommended)
      const { data: manifest, error: manifestError } = await supabase
        .from('manifests')
        .select('id')
        .eq('blueprint_id', blueprintId)
        .maybeSingle();

      if (manifestError && manifestError.code !== 'PGRST116') {
        logger.warn('Error checking manifest', {
          error: manifestError,
          blueprintId,
        });
        // Continue anyway - manifest is optional
      }

      if (!manifest) {
        // Use promise-based modal approach
        const proceed = await new Promise<boolean>((resolve) => {
          setManifestConfirm({
            isOpen: true,
            resolvePromise: resolve,
          });
        });
        
        if (!proceed) {
          return;
        }
      }

      // 3. Check GitHub connection
      const { data: githubConnection, error: githubError } = await supabase
        .from('user_connections')
        .select('id')
        .eq('user_id', userId)
        .eq('provider', 'github')
        .eq('status', 'active')
        .maybeSingle();

      if (githubError && githubError.code !== 'PGRST116') {
        logger.warn('Error checking GitHub connection', {
          error: githubError,
          userId,
        });
      }

      if (!githubConnection) {
        toast.error(
          'GitHub connection required. Please connect your GitHub account in Settings.'
        );
        return;
      }

      // 4. Check project has repository configured
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select('repository_url, github_owner, github_repo_name')
        .eq('id', projectId)
        .maybeSingle();

      if (projectError) {
        logger.error('Error checking project', {
          error: projectError,
          projectId,
        });
        toast.error('Failed to verify project configuration');
        return;
      }

      if (
        !project?.repository_url &&
        !(project?.github_owner && project?.github_repo_name)
      ) {
        toast.error(
          'Project must have a GitHub repository configured before starting Build Mode'
        );
        return;
      }

      // All validations passed - proceed with Build Mode initialization
      // Import OrchestratorAIService dynamically to avoid circular dependencies
      const { OrchestratorAIService } = await import(
        '../../services/orchestratorAIService'
      );

      // Create build session
      const session = await OrchestratorAIService.initializeBuildSession(
        blueprintId,
        projectId,
        userId
      );

      // Store session ID in App state (pass via callback prop)
      onBuildModeStarted?.(session.id);

      // Navigate to Build Mode
      // (handled by parent App component state change)
      toast.success('Build Mode started!');
    } catch (error) {
      logger.error('Failed to start Build Mode', {
        error,
        blueprintId,
        projectId,
        userId,
      });
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to start Build Mode';
      toast.error(errorMessage);
    }
  };

  const steps = [
    { id: 'requirements', label: 'Requirements', icon: '📝' },
    { id: 'design-system', label: 'Design System', icon: '🎨' },
    { id: 'blueprint', label: 'Blueprint', icon: '📐' },
    { id: 'wireframes-low', label: 'Wireframes (Draft)', icon: '📱' },
    { id: 'wireframes-high', label: 'Wireframes (Final)', icon: '🖼️' },
    { id: 'manifest', label: 'Manifest', icon: '📄' },
    { id: 'jira', label: 'Jira Plan', icon: '📋' },
    { id: 'scaffold', label: 'Scaffold', icon: '🚀' },
    { id: 'complete', label: 'Complete', icon: '✅' },
  ];

  return (
    <PlanModeErrorBoundary>
      <div className="min-h-screen flex bg-background-secondary">
        {/* Sidebar */}
        <PromptSidebar
          projectId={projectId}
          onNodeSelect={handleNodeSelect}
          onNodeUpdate={handleNodeUpdate}
          selectedNodeId={selectedNode?.id}
          className="w-80 flex-shrink-0"
        />

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col">
          {/* Progress Header */}
          <div
            className="shadow-sm border-b"
            style={{
              backgroundColor: 'rgb(var(--color-background))',
              borderColor: 'rgb(var(--color-border))',
            }}
          >
            <div className="max-w-7xl mx-auto" style={{ padding: spacing.page.header }}>
              <Heading level={1} className="mb-4">
                Plan Mode
              </Heading>
              <ProgressStepper steps={steps} currentStep={currentStep} />
            </div>
          </div>

          {/* Navigation */}
          {currentStep !== 'requirements' && (
            <div
              className="border-b"
              style={{
                backgroundColor: 'rgb(var(--color-background))',
                borderColor: 'rgb(var(--color-border))',
              }}
            >
              <div className="max-w-7xl mx-auto" style={{ padding: spacing.section.nav }}>
                <div className="flex gap-2">
                  {blueprintId && (
                    <Button
                      variant={currentStep === 'blueprint' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setCurrentStep('blueprint')}
                      className={cn(
                        currentStep === 'blueprint' && 'bg-primary-light text-primary'
                      )}
                    >
                      Blueprint
                    </Button>
                  )}
                  {manifestId && (
                    <Button
                      variant={currentStep === 'manifest' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setCurrentStep('manifest')}
                      className={cn(
                        currentStep === 'manifest' && 'bg-primary-light text-primary'
                      )}
                    >
                      Manifest
                    </Button>
                  )}
                  {blueprintId && (
                    <Button
                      variant={currentStep === 'jira' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setCurrentStep('jira')}
                      className={cn(
                        currentStep === 'jira' && 'bg-primary-light text-primary'
                      )}
                    >
                      Jira Plan
                    </Button>
                  )}
                  {manifestId && (
                    <Button
                      variant={currentStep === 'scaffold' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setCurrentStep('scaffold')}
                      className={cn(
                        currentStep === 'scaffold' && 'bg-primary-light text-primary'
                      )}
                    >
                      Scaffold
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          <div style={{ padding: spacing.page.content }}>
            {loadingProject ? (
              <LoadingState message="Loading project..." size="md" />
            ) : currentStep === 'requirements' ? (
              project?.import_status === 'analyzing' ? (
                <div className='max-w-5xl mx-auto px-6'>
                  <AnalysisProgressIndicator
                    projectId={projectId}
                    onComplete={() => {
                      loadProject();
                      toast.success('Analysis complete! Ready to plan.');
                    }}
                  />
                </div>
              ) : (
                <div className="h-screen flex">
                  {/* Mode Selector */}
                  <div
                    className="w-64 border-r"
                    style={{
                      backgroundColor: 'rgb(var(--color-background))',
                      borderColor: 'rgb(var(--color-border))',
                      padding: spacing.card.padding,
                    }}
                  >
                    <Heading level={3} className="mb-4">
                      Requirements Mode
                    </Heading>
                    <div className="space-y-2">
                      <Button
                        variant={requirementsMode === 'conversational' ? 'default' : 'ghost'}
                        className={cn(
                          'w-full justify-start text-left',
                          requirementsMode === 'conversational' && 'bg-primary-light text-primary'
                        )}
                        onClick={() => setRequirementsMode('conversational')}
                      >
                        <div className="flex flex-col items-start">
                          <Text weight="medium">💬 Conversational</Text>
                          <Text size="sm" className="opacity-75">
                            Chat with AI to define requirements
                          </Text>
                        </div>
                      </Button>
                      <Button
                        variant={requirementsMode === 'form' ? 'default' : 'ghost'}
                        className={cn(
                          'w-full justify-start text-left',
                          requirementsMode === 'form' && 'bg-primary-light text-primary'
                        )}
                        onClick={() => setRequirementsMode('form')}
                      >
                        <div className="flex flex-col items-start">
                          <Text weight="medium">📝 Traditional Form</Text>
                          <Text size="sm" className="opacity-75">
                            Fill out structured forms
                          </Text>
                        </div>
                      </Button>
                    </div>

                    {project?.import_status === 'analysis_complete' ||
                    project?.auto_populated ? (
                      <div className="mt-6">
                        <Button
                          variant="ghost"
                          className="w-full justify-start text-left bg-success-light text-success hover:bg-success-light/80"
                          onClick={() => setRequirementsMode('conversational')}
                        >
                          <div className="flex flex-col items-start">
                            <Text weight="medium">🤖 Smart Planning</Text>
                            <Text size="sm" className="opacity-75">
                              AI-powered planning interface
                            </Text>
                          </div>
                        </Button>
                      </div>
                    ) : null}
                  </div>

                  {/* Main Content */}
                  <div className='flex-1 flex'>
                    {requirementsMode === 'conversational' ? (
                      <>
                        <PromptSidebar
                          projectId={projectId}
                          onItemClick={item => {
                            // Handle item click - could show details or focus prompt
                            logger.debug('Item clicked', { item });
                          }}
                          onPromptFocus={prompt => {
                            setFocusedPrompt(prompt);
                          }}
                        />
                        <ConversationalRequirements
                          projectId={projectId}
                          userId={userId}
                        />
                      </>
                    ) : project?.import_status === 'analysis_complete' ||
                      project?.auto_populated ? (
                      <SmartPlanningInterface
                        projectId={projectId}
                        userId={userId}
                        onBlueprintGenerated={handleBlueprintGenerated}
                      />
                    ) : (
                      <RequirementsForm
                        projectId={projectId}
                        userId={userId}
                        onBlueprintGenerated={handleBlueprintGenerated}
                      />
                    )}
                  </div>
                </div>
              )
            ) : currentStep === 'design-system' ? (
              <DesignSystemConfig
                projectId={projectId}
                blueprintId={blueprintId!}
                onComplete={handleDesignSystemComplete}
              />
            ) : currentStep === 'wireframes-low' ||
              currentStep === 'wireframes-high' ? (
              wireframeMode === 'gallery' ? (
                <WireframeGallery
                  projectId={projectId}
                  userId={userId}
                  onEditWireframe={handleEditWireframe}
                  onViewWireframe={handleViewWireframe}
                  onCreateWireframe={handleCreateWireframe}
                />
              ) : (
                <WireframeEditor
                  projectId={projectId}
                  userId={userId}
                  wireframeId={editingWireframe?.id}
                  onSave={handleWireframeSaved}
                  onClose={handleCloseWireframeEditor}
                />
              )
            ) : null}

            {currentStep === 'blueprint' && blueprintId && (
              <Suspense
                fallback={
                  <LoadingState message="Loading blueprint..." size="md" />
                }
              >
                <BlueprintDashboard
                  blueprintId={blueprintId!}
                  projectId={projectId}
                  userId={userId}
                  jiraProjectKey={jiraProjectKey}
                  onFreeze={handleBlueprintFrozen}
                  onGenerateManifest={handleGenerateManifest}
                  onGenerateJiraPlan={handleGenerateJiraPlan}
                  onStartBuildMode={handleStartBuildMode}
                />
              </Suspense>
            )}

            {currentStep === 'manifest' && manifestId && (
              <ManifestViewer
                manifestId={manifestId}
                onScaffold={handleScaffold}
              />
            )}

            {currentStep === 'jira' && blueprintId && (
              <JiraPlanViewer
                projectId={projectId}
                userId={userId}
                blueprintId={blueprintId!}
                jiraProjectKey={jiraProjectKey}
              />
            )}

            {currentStep === 'scaffold' && manifestId && (
              <GitHubScaffoldPanel
                manifestId={manifestId}
                userId={userId}
                projectId={projectId}
              />
            )}

            {currentStep === 'complete' && (
              <div className="max-w-4xl mx-auto" style={{ padding: spacing.page.content }}>
                <Card className="text-center" style={{ padding: spacing.card.large }}>
                  <div className="text-6xl mb-4">🎉</div>
                  <Heading level={2} className="mb-4">
                    Plan Mode Complete!
                  </Heading>
                  <Text size="lg" className="mb-8 text-text-secondary">
                    Your project is now ready for Agent Mode execution.
                  </Text>
                  <div className="space-y-4">
                    <Card
                      className="text-left"
                      style={{
                        backgroundColor: 'rgb(var(--color-success-light))',
                        borderColor: 'rgb(var(--color-success))',
                        padding: spacing.card.padding,
                      }}
                    >
                      <Heading level={4} className="mb-2 text-success">
                        ✓ Blueprint Frozen
                      </Heading>
                      <Text size="sm" className="text-success">
                        Architecture and specifications are locked
                      </Text>
                    </Card>
                    <Card
                      className="text-left"
                      style={{
                        backgroundColor: 'rgb(var(--color-success-light))',
                        borderColor: 'rgb(var(--color-success))',
                        padding: spacing.card.padding,
                      }}
                    >
                      <Heading level={4} className="mb-2 text-success">
                        ✓ Manifest Generated
                      </Heading>
                      <Text size="sm" className="text-success">
                        Build instructions and file templates ready
                      </Text>
                    </Card>
                    <Card
                      className="text-left"
                      style={{
                        backgroundColor: 'rgb(var(--color-success-light))',
                        borderColor: 'rgb(var(--color-success))',
                        padding: spacing.card.padding,
                      }}
                    >
                      <Heading level={4} className="mb-2 text-success">
                        ✓ Jira Plan Created
                      </Heading>
                      <Text size="sm" className="text-success">
                        Epics and stories synchronized with Jira
                      </Text>
                    </Card>
                    <Card
                      className="text-left"
                      style={{
                        backgroundColor: 'rgb(var(--color-success-light))',
                        borderColor: 'rgb(var(--color-success))',
                        padding: spacing.card.padding,
                      }}
                    >
                      <Heading level={4} className="mb-2 text-success">
                        ✓ Repository Scaffolded
                      </Heading>
                      <Text size="sm" className="text-success">
                        GitHub repository initialized with structure and CI/CD
                      </Text>
                    </Card>
                  </div>
                  <Button size="lg" className="mt-8">
                    Start Agent Mode →
                  </Button>
                </Card>
              </div>
            )}
          </div>

          {/* Drill-Down Interface */}
          {showDrillDown && (
            <DrillDownPromptInterface
              selectedNode={selectedNode}
              nodeData={nodeData}
              promptContext={promptContext}
              suggestions={suggestions}
              onNodeUpdate={handleNodeUpdate}
              onClose={handleCloseDrillDown}
            />
          )}

          {/* Help Panel */}
          <HelpPanel step={currentStep} />

          {/* Loading Overlay */}
          {isGenerating && (
            <LoadingOverlay
              message='Generating Manifest...'
              submessage='This may take a few moments'
            />
          )}
        </div>
      </div>
      
      <ConfirmModal
        isOpen={manifestConfirm.isOpen}
        onClose={() => {
          if (manifestConfirm.resolvePromise) {
            manifestConfirm.resolvePromise(false);
          }
          setManifestConfirm({ isOpen: false, resolvePromise: null });
        }}
        onConfirm={() => {
          if (manifestConfirm.resolvePromise) {
            manifestConfirm.resolvePromise(true);
          }
          setManifestConfirm({ isOpen: false, resolvePromise: null });
        }}
        title="Manifest Not Found"
        message="No manifest found. It is recommended to generate a manifest before starting Build Mode. Continue anyway?"
        confirmText="Continue"
        cancelText="Cancel"
        confirmVariant="primary"
        loading={false}
      />
    </PlanModeErrorBoundary>
  );
}
